import React, { useEffect, useMemo, useRef } from 'react'
import type { Path } from 'react-hook-form'
import { useForm, useWatch } from 'react-hook-form'
import { useQueryClient } from '@tanstack/react-query'
import { zodResolver } from '@hookform/resolvers/zod'
import {
  Alert,
  Button,
  Popconfirm,
  Tooltip,
} from '@surecompanies/core_components'
import { useDebounce } from 'hooks/useDebounce'
import { relativeTimeString } from 'enrollment-platform/utils/relative-time-string'
import {
  HIOS_PLAN_ID_REGEX,
  PlanBenefitOverrideFormSchema,
  PlanBenefitOverrideFormValues,
  TEMPLATE_PARAM_DEFAULTS,
  formParamsToApiParams,
  type ViewMetadata,
} from './planBenefitOverrideForm.schema'
import ChangeValueCard from './PlanBenefitOverrideForm.ChangeValueCard'
import { PARTIAL_CHIPS } from './PartialFieldChipSelector'
import OverrideAuditStrip from './OverrideAuditStrip'
import { PlanBenefitOverrideRuleTemplateKey } from '../enums/plan-benefit-override-rule.enum'
import { useGetPlanBenefits } from '../hooks/useGetPlanBenefits'
import {
  useCheckDuplicateOverrideRule,
  OVERRIDE_RULE_CHECK_DUPLICATE_QUERY_KEY,
} from '../hooks/useCheckDuplicateOverrideRule'
import { useDeleteOverrideRule } from '../hooks/useDeleteOverrideRule'
import type { CreatePlanBenefitOverrideRuleRequestDto } from '../../../api/dto/plan-benefit-override-rules.dto'
import styles from './PlanBenefitOverrideForm.module.scss'

const DUPLICATE_CHECK_DEBOUNCE_MS = 400

function collectErrorMessages(obj: unknown): string[] {
  if (!obj || typeof obj !== 'object') return []
  const messages: string[] = []
  for (const value of Object.values(obj as Record<string, unknown>)) {
    if (!value || typeof value !== 'object') continue
    if (
      'message' in value &&
      typeof (value as { message: unknown }).message === 'string'
    ) {
      const msg = (value as { message: string }).message
      if (msg) messages.push(msg)
    } else {
      messages.push(...collectErrorMessages(value))
    }
  }
  return messages
}

export type PlanBenefitOverrideFormMode = 'create' | 'edit' | 'view'

export interface ServerFieldError {
  field: string
  errors: string[]
}

type Props = {
  mode?: PlanBenefitOverrideFormMode
  defaultValues?: Partial<PlanBenefitOverrideFormValues>
  serverErrors?: ServerFieldError[]
  metadata?: ViewMetadata
  onSubmit?: (data: CreatePlanBenefitOverrideRuleRequestDto) => Promise<void>
  onCancel?: () => void
  isSubmitting?: boolean
  onDuplicateFound?: (ruleId: string) => void
  extraContent?: React.ReactNode
}

function mapServerFieldToFormField(
  field: string
): Path<PlanBenefitOverrideFormValues> | null {
  if (!field) return null
  if (field.startsWith('match.')) {
    return field.slice('match.'.length) as Path<PlanBenefitOverrideFormValues>
  }
  return field as Path<PlanBenefitOverrideFormValues>
}

export default function PlanBenefitOverrideForm({
  mode = 'create',
  defaultValues,
  serverErrors,
  metadata,
  onSubmit,
  onCancel,
  isSubmitting = false,
  onDuplicateFound,
  extraContent,
}: Props) {
  const isEditMode = mode === 'edit'
  const isReadOnly = mode === 'view'
  const {
    control,
    handleSubmit,
    setValue,
    setError,
    formState: { errors, submitCount },
  } = useForm<PlanBenefitOverrideFormValues>({
    resolver: zodResolver(PlanBenefitOverrideFormSchema),
    defaultValues: {
      template: PlanBenefitOverrideRuleTemplateKey.PARTIAL_FIELD_UPDATE,
      hiosPlanId: '',
      year: new Date().getFullYear(),
      benefitName: '',
      sourceUrl: '',
      notes: '',
      params: {},
      ...defaultValues,
    } as Partial<PlanBenefitOverrideFormValues>,
  })

  useEffect(() => {
    if (!serverErrors || serverErrors.length === 0) return
    serverErrors.forEach(({ field, errors: fieldErrors }) => {
      const formField = mapServerFieldToFormField(field)
      if (!formField || fieldErrors.length === 0) return
      setError(formField, { type: 'server', message: fieldErrors.join(', ') })
    })
  }, [serverErrors, setError])

  const template = useWatch({ control, name: 'template' })
  const hiosPlanId = useWatch({ control, name: 'hiosPlanId' })
  const year = useWatch({ control, name: 'year' })
  const benefitName = useWatch({ control, name: 'benefitName' })
  const params = useWatch({ control, name: 'params' })

  const isPartialFieldUpdate =
    template === PlanBenefitOverrideRuleTemplateKey.PARTIAL_FIELD_UPDATE
  const hasSelectedFields = isPartialFieldUpdate
    ? Object.values(params ?? {}).some((v) => v !== undefined)
    : true

  const duplicateKey =
    mode === 'create' &&
    hiosPlanId &&
    HIOS_PLAN_ID_REGEX.test(hiosPlanId) &&
    year &&
    benefitName
      ? `${hiosPlanId}|${year}|${benefitName}`
      : null
  const debouncedDuplicateKey = useDebounce(
    duplicateKey,
    DUPLICATE_CHECK_DEBOUNCE_MS
  )
  const duplicateQuery = debouncedDuplicateKey
    ? { hiosPlanId, year: Number(year), benefitName }
    : {}
  const { data: duplicateData } = useCheckDuplicateOverrideRule(
    duplicateQuery,
    Boolean(debouncedDuplicateKey)
  )
  const showDuplicateAlert = duplicateData?.exists === true
  const duplicateRule = duplicateData?.exists ? duplicateData.rule : null

  const queryClient = useQueryClient()
  const { mutate: deleteDuplicate, isPending: isDeletingDuplicate } =
    useDeleteOverrideRule()
  const handleDeleteDuplicate = () => {
    if (!duplicateRule) return
    deleteDuplicate(duplicateRule.id, {
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: [OVERRIDE_RULE_CHECK_DUPLICATE_QUERY_KEY],
        })
      },
      onError: (err) => {
        // eslint-disable-next-line no-console
        console.error('delete duplicate rule failed', err)
      },
    })
  }

  const {
    benefitsByName,
    isLoading: isBenefitsLoading,
    error: benefitsError,
    refetch: refetchBenefits,
  } = useGetPlanBenefits()
  const metadataError = benefitsError ?? null

  useEffect(() => {
    if (!benefitsError) return
    // eslint-disable-next-line no-console
    console.error('plan-benefit-override benefits load failed', benefitsError)
  }, [benefitsError])

  // Stable refs so the template-reset effect can read latest values without
  // adding them as deps (would re-run on every benefit load).
  const benefitsByNameRef = useRef(benefitsByName)
  benefitsByNameRef.current = benefitsByName
  const benefitNameRef = useRef(benefitName)
  benefitNameRef.current = benefitName

  // Reset params on template switch. Includes sort for UPSERT_RULE when a
  // benefit is already selected — child effects (ChangeValueCard) run before
  // parent effects in React, so without this the parent reset would wipe sort.
  useEffect(() => {
    if (!template || mode !== 'create') return
    const defaults: Record<string, unknown> = {
      ...(TEMPLATE_PARAM_DEFAULTS[template] ?? {}),
    }
    if (
      template === PlanBenefitOverrideRuleTemplateKey.UPSERT_RULE &&
      benefitNameRef.current
    ) {
      const benefit =
        benefitsByNameRef.current[
          benefitNameRef.current as keyof typeof benefitsByNameRef.current
        ]
      if (benefit?.sortOrder != null) {
        defaults.sort = benefit.sortOrder
      }
    }
    setValue('params', defaults as PlanBenefitOverrideFormValues['params'])
    selectedPartialFieldsRef.current = new Set()
  }, [template, setValue, mode])

  const initialSelectedFields = useMemo(() => {
    if (
      defaultValues?.template !==
      PlanBenefitOverrideRuleTemplateKey.PARTIAL_FIELD_UPDATE
    )
      return undefined
    const p = defaultValues?.params
    if (!p || typeof p !== 'object') return undefined
    return Object.keys(p).filter(
      (k) => (p as Record<string, unknown>)[k] !== undefined
    )
  }, [defaultValues])

  const selectedPartialFieldsRef = useRef<Set<string>>(new Set())
  useEffect(() => {
    selectedPartialFieldsRef.current = new Set(initialSelectedFields ?? [])
  }, [initialSelectedFields])

  const handleFormSubmit = async (values: PlanBenefitOverrideFormValues) => {
    if (!onSubmit) return

    if (
      values.template ===
      PlanBenefitOverrideRuleTemplateKey.PARTIAL_FIELD_UPDATE
    ) {
      const selected = selectedPartialFieldsRef.current
      let hasEmptyFields = false

      for (const chip of PARTIAL_CHIPS) {
        if (!selected.has(chip.name) || chip.type === 'boolean') continue
        const val = (values.params as Record<string, unknown>)[chip.name]
        if (val === undefined || val === null || val === '') {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          setError(`params.${chip.name}` as any, {
            type: 'manual',
            message: 'Required',
          })
          hasEmptyFields = true
        }
      }

      if (hasEmptyFields) return

      if (selected.size === 0) {
        setError('root', {
          type: 'manual',
          message: 'Please select at least one field to override.',
        })
        return
      }
    }

    const apiParams = formParamsToApiParams(
      values.template,
      values.params as Record<string, unknown>
    )

    if (
      values.template ===
        PlanBenefitOverrideRuleTemplateKey.PARTIAL_FIELD_UPDATE &&
      Object.keys(apiParams).length === 0
    ) {
      setError('root', {
        type: 'manual',
        message: 'Please select at least one field to override.',
      })
      return
    }

    const requestBody: CreatePlanBenefitOverrideRuleRequestDto = {
      template: values.template,
      params: apiParams,
      match: {
        hiosPlanId: values.hiosPlanId,
        year: Number(values.year),
        benefitName: values.benefitName,
      },
      ...(values.sourceUrl ? { sourceUrl: values.sourceUrl } : {}),
      ...(values.notes ? { notes: values.notes } : {}),
    }
    await onSubmit(requestBody)
  }

  const formErrorAlert =
    submitCount > 0 && Object.keys(errors).length > 0 ? (
      <Alert
        type="error"
        showIcon
        message={
          errors.root?.message ?? 'Please fix the errors below before saving.'
        }
        description={(() => {
          const fieldMessages = errors.root ? [] : collectErrorMessages(errors)
          return fieldMessages.length > 0 ? (
            <ul className={styles.errorList}>
              {fieldMessages.map((msg, i) => (
                // eslint-disable-next-line react/no-array-index-key
                <li key={i}>{msg}</li>
              ))}
            </ul>
          ) : undefined
        })()}
        className={styles.alertSpacing}
      />
    ) : null

  return (
    <form
      onSubmit={handleSubmit(handleFormSubmit)}
      noValidate
      className={isReadOnly || isEditMode ? styles.readOnlyForm : undefined}
    >
      {!isReadOnly && showDuplicateAlert && duplicateRule && (
        <div className={styles.duplicateBanner}>
          <div className={styles.duplicateBannerIcon}>⚠</div>
          <div className={styles.duplicateBannerBody}>
            <span className={styles.duplicateBannerTitle}>
              Rule already exists for this combination
            </span>
            <span className={styles.duplicateBannerValue}>
              Plan <strong>{duplicateRule.match.hiosPlanId}</strong>
              <span className={styles.duplicateBannerDot}>·</span>
              {benefitsByName[duplicateRule.match.benefitName]?.displayName ??
                duplicateRule.match.benefitName}
              <span className={styles.duplicateBannerDot}>·</span>
              {duplicateRule.match.year}
            </span>
            <span className={styles.duplicateBannerMeta}>
              <span
                className={`${styles.statusTag} ${duplicateRule.status === 'APPROVED' ? styles.statusActive : duplicateRule.status === 'REJECTED' ? styles.statusInactive : styles.statusDraft}`}
              >
                {duplicateRule.status}
              </span>
              <span className={styles.duplicateBannerDot}>·</span>
              {relativeTimeString(duplicateRule.updatedAt)}
            </span>
          </div>
          <div className={styles.duplicateBannerActions}>
            <Button
              type="primary"
              size="small"
              onClick={() => onDuplicateFound?.(duplicateRule.id)}
              disabled={!onDuplicateFound}
            >
              Edit
            </Button>
            <Popconfirm
              title="Delete this rule?"
              description="This will permanently delete the existing rule."
              okText="Delete"
              okType="danger"
              cancelText="Cancel"
              onConfirm={handleDeleteDuplicate}
            >
              <Button
                type="default"
                size="small"
                danger
                loading={isDeletingDuplicate}
              >
                Delete
              </Button>
            </Popconfirm>
          </div>
        </div>
      )}

      {formErrorAlert}

      <ChangeValueCard
        control={control}
        setValue={setValue}
        isEditMode={isEditMode}
        isReadOnly={isReadOnly}
        initialSelectedFields={initialSelectedFields}
        onPartialSelectionChange={(selected) => {
          selectedPartialFieldsRef.current = selected
        }}
      />

      {extraContent && (
        <div className={styles.extraContentSection}>{extraContent}</div>
      )}

      {metadataError && (
        <Alert
          type="error"
          showIcon
          message="We couldn't load the benefits list."
          description={
            <Button
              type="link"
              onClick={() => refetchBenefits()}
              className={styles.retryButton}
            >
              Try again
            </Button>
          }
          className={styles.alertSpacing}
        />
      )}

      {!isReadOnly && (
        <div className={styles.footer}>
          {isEditMode && metadata && <OverrideAuditStrip metadata={metadata} />}
          <div className={styles.footerActions}>
            <Button type="default" onClick={onCancel} disabled={isSubmitting}>
              Cancel
            </Button>
            <Tooltip
              title={
                showDuplicateAlert
                  ? 'Resolve the duplicate rule above before saving.'
                  : metadataError
                    ? 'Cannot save — benefit list failed to load.'
                    : !hiosPlanId || !HIOS_PLAN_ID_REGEX.test(hiosPlanId)
                      ? 'Enter a valid HIOS Plan ID before saving.'
                      : !benefitName
                        ? 'Select a benefit type before saving.'
                        : !hasSelectedFields
                          ? 'Select at least one field to override.'
                          : undefined
              }
            >
              <Button
                type="primary"
                htmlType="submit"
                loading={isSubmitting}
                disabled={
                  isSubmitting ||
                  isBenefitsLoading ||
                  Boolean(metadataError) ||
                  showDuplicateAlert ||
                  !hiosPlanId ||
                  !HIOS_PLAN_ID_REGEX.test(hiosPlanId) ||
                  !benefitName ||
                  !hasSelectedFields
                }
              >
                Save
              </Button>
            </Tooltip>
          </div>
        </div>
      )}
    </form>
  )
}
